import 'react';
import {
    Box,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {A12_ROWS, A12Row, A_PAGE_TITLES} from '@/pages/components/audit-program/a-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';
import ProgramDateField from '@/pages/components/audit-program/ProgramDateField';
import {resolveDisplayLevel} from '@/pages/components/audit-program/templates/rowLevel';

export interface A12SubsequentEventsViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
    title?: string;
    rows?: A12Row[];
}

const YN_OPTIONS = ['', 'Y', 'N', 'N/A'];

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.75rem',
    lineHeight: 1.25,
    verticalAlign: 'bottom' as const,
    py: 0.75,
    px: 0.5,
    whiteSpace: 'normal' as const,
};
/** Contain InputField default minWidth:110 so adjacent cells don't overlap */
const bodySx = {
    ...cellBorder,
    py: 0.5,
    px: 0.5,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

function YnSelect({value, onChange}: {value?: string; onChange: (v: string) => void}) {
    return (
        <InputField
            select
            compact
            fullWidth
            align="left"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            sx={fieldSx}
        >
            {YN_OPTIONS.map((opt) => (
                <MenuItem key={opt || 'blank'} value={opt}>{opt || '—'}</MenuItem>
            ))}
        </InputField>
    );
}

function CompactInput({
    value,
    onChange,
    align = 'left',
}: {
    value: string;
    onChange: (v: string) => void;
    align?: 'left' | 'center' | 'right';
}) {
    return (
        <InputField
            type="text"
            fullWidth
            compact
            align={align}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            sx={fieldSx}
        />
    );
}

/**
 * Layout mirrors Excel sheet A1.2:
 * Header → centered title → procedure table (Y/N + Initials + Date) → HKSA notes
 */
export default function A12SubsequentEventsView({
    header,
    values,
    onChange,
    title,
    rows,
}: A12SubsequentEventsViewProps) {
    const pageTitle = title ?? A_PAGE_TITLES['A1.2'].toUpperCase();
    const pageRows = rows ?? A12_ROWS;
    const rowField = (rowId: string, field: string) => values[`${rowId}.${field}`] ?? '';

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode="A1.2"
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1.5, mt: 0.5}}
            >
                {pageTitle}
            </Typography>

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 1200, tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0}}>
                    <colgroup>
                        <col style={{width: 40}}/>
                        <col style={{width: '30%'}}/>
                        <col style={{width: 100}}/>
                        <col style={{width: 110}}/>
                        <col style={{width: 90}}/>
                        <col style={{width: 160}}/>
                        <col style={{width: 90}}/>
                        <col style={{width: 120}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} colSpan={2}/>
                            <TableCell sx={headSx} align="center">Test required Y/N</TableCell>
                            <TableCell sx={headSx} align="center">Results satisfactory Y/N</TableCell>
                            <TableCell sx={headSx} align="center">Sch ref</TableCell>
                            <TableCell sx={headSx} align="center">Comments</TableCell>
                            <TableCell sx={headSx} align="center">Initials</TableCell>
                            <TableCell sx={headSx} align="center">Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.map((row) => {
                            if (row.kind === 'general') {
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx} colSpan={2}>
                                            <Typography variant="body2" fontWeight={700}>{row.text}</Typography>
                                        </TableCell>
                                        <TableCell sx={bodySx} colSpan={4}/>
                                        <TableCell sx={bodySx}>
                                            <CompactInput
                                                value={values.generalInitials ?? header.reviewedInitial ?? header.preparedInitial ?? ''}
                                                onChange={(v) => onChange('generalInitials', v)}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodySx}>
                                            <ProgramDateField
                                                value={values.generalDate ?? ''}
                                                onChange={(v) => onChange('generalDate', v)}
                                                caption={false}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                            if (row.kind === 'na') {
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx}/>
                                        <TableCell sx={{...bodySx, pl: 4}} colSpan={7}>
                                            <Typography variant="body2" color="text.secondary">{row.text}</Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                            if (row.kind === 'note') {
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx} colSpan={8}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{whiteSpace: 'pre-line', display: 'block', py: 0.5}}
                                            >
                                                {row.text}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            }

                            const level = resolveDisplayLevel(row);
                            // Stronger than B checklist: A1.2 lettered (a)–(j) need clear hierarchy under item 4.
                            const pl = level <= 0 ? 0 : level === 1 ? 2.5 : 4;
                            return (
                                <TableRow key={row.id}>
                                    <TableCell sx={bodySx}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={level >= 1 ? 400 : 600}
                                            sx={{pl}}
                                        >
                                            {row.num}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={bodySx}>
                                        <Typography variant="body2" sx={{lineHeight: 1.4, pl}}>
                                            {row.text}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={bodySx}>
                                        <YnSelect
                                            value={rowField(row.id, 'testRequired')}
                                            onChange={(v) => onChange(`${row.id}.testRequired`, v)}
                                        />
                                    </TableCell>
                                    <TableCell sx={bodySx}>
                                        <YnSelect
                                            value={rowField(row.id, 'resultsSatisfactory')}
                                            onChange={(v) => onChange(`${row.id}.resultsSatisfactory`, v)}
                                        />
                                    </TableCell>
                                    <TableCell sx={bodySx}>
                                        <CompactInput
                                            value={rowField(row.id, 'schRef')}
                                            onChange={(v) => onChange(`${row.id}.schRef`, v)}
                                            align="center"
                                        />
                                    </TableCell>
                                    <TableCell sx={bodySx}>
                                        <CompactInput
                                            value={rowField(row.id, 'comments')}
                                            onChange={(v) => onChange(`${row.id}.comments`, v)}
                                        />
                                    </TableCell>
                                    <TableCell sx={bodySx}>
                                        <CompactInput
                                            value={
                                                rowField(row.id, 'initialsDate')
                                                || header.reviewedInitial
                                                || header.preparedInitial
                                                || ''
                                            }
                                            onChange={(v) => onChange(`${row.id}.initialsDate`, v)}
                                        />
                                    </TableCell>
                                    <TableCell sx={bodySx}>
                                        <ProgramDateField
                                            value={rowField(row.id, 'date')}
                                            onChange={(v) => onChange(`${row.id}.date`, v)}
                                            caption={false}
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
